import { MinusCircleFilled, PlusCircleFilled } from '@ant-design/icons'
import { Checkbox, Switch } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import Utils from '@utils/utils'
import styles from './index.module.scss'

const MAX_COUNT = 8

const INITIAL_ITEMS = [
    { key: 'poster', label: '首屏海报', value: 1, min: 1, fixed: true, comp: 0 },
    { key: 'sellingPoint', label: '产品卖点', value: 1, min: 1, comp: 1 },
    { key: 'buyerShow', label: '痛点解决', value: 1, min: 1, checked: true, comp: 2 },
    { key: 'sceneStyling', label: '场景化穿搭', value: 1, min: 1, checked: true, comp: 3 },
    { key: 'productInfo', label: '产品信息', value: 1, min: 1, checked: true, comp: 4 },
    { key: 'productDetail', label: '产品细节', value: 2, min: 1, checked: true, comp: 5 },
    { key: 'sizeChart', label: '尺码表', value: 1, min: 1, checked: true, comp: 6 },
    // { key: 'modelImage', label: '模特图', value: 2, min: 0, checked: false, showModeRow: true, comp: 7 },
    { key: 'serviceGuarantee', label: '购物保障', value: 1, min: 1, checked: true, comp: 7 },
]

const normalizeItems = (items = []) => {
    return items.map((item) => {
        const min = Math.max(Number(item?.min) || 1, 1)
        const nextValue = Number(item?.value)
        const value = Math.max(min, Math.min(MAX_COUNT, Number.isFinite(nextValue) ? nextValue : min))
        return {
            ...item,
            min,
            value,
        }
    })
}

const isSameItemList = (listA = [], listB = []) => {
    if (listA.length !== listB.length) return false
    return listA.every((item, index) => {
        const nextItem = listB[index]
        return item?.key === nextItem?.key &&
            item?.value === nextItem?.value &&
            item?.min === nextItem?.min &&
            item?.checked === nextItem?.checked &&
            item?.fixed === nextItem?.fixed
    })
}

const CounterAction = ({ disabled, type, onClick }) => {
    const className = disabled ? styles.CounterIconDisabled : styles.CounterIcon
    const Icon = type === 'minus' ? MinusCircleFilled : PlusCircleFilled

    return (
        <button
            type="button"
            className={styles.CounterButton}
            disabled={disabled}
            onClick={onClick}
        >
            <Icon className={className} />
        </button>
    )
}

const CustomDetailPageConfig = ({ value, onOpenModelDrawer, onClearSelectedModel, hasSelectedModel, selectedModelUrl, onItemListChange }) => {
    const [itemList, setItemList] = useState(() => normalizeItems(value?.length ? value : INITIAL_ITEMS))
    const [smartMode, setSmartMode] = useState(true)

    useEffect(() => {
        if (!value?.length) return
        const normalizedValue = normalizeItems(value)
        setItemList((prev) => isSameItemList(prev, normalizedValue) ? prev : normalizedValue)
    }, [value])

    const displayRows = useMemo(() => {
        return itemList
            .filter((item) => item.key !== 'modelImage')
            .flatMap((item) => {
            const rows = [{ type: 'item', item }]
            // 暂时隐藏“智能模式”配置行
            if (false && item.showModeRow) {
                rows.push({ type: 'modelMode', key: `${item.key}-mode` })
            }
            return rows
        })
    }, [itemList])

    const updateValue = (key, delta) => {
        setItemList((prev) => normalizeItems(prev.map((item) => {
            if (item.key !== key || item.fixed || item.checked === false) return item
            const nextValue = Math.max(item.min, Math.min(MAX_COUNT, item.value + delta))
            return { ...item, value: nextValue }
        })))
    }

    const toggleItemChecked = (key) => {
        setItemList((prev) => normalizeItems(prev.map((item) => {
            if (item.key !== key || item.fixed || item.checked === undefined) return item
            return { ...item, checked: !item.checked }
        })))
    }

    const modelImageItem = useMemo(() => {
        return itemList.find((item) => item.key === 'modelImage')
    }, [itemList])

    const modelImageCount = modelImageItem?.value || 0
    const modelModeDisabled = modelImageCount <= 0 || modelImageItem?.checked === false
    const canOpenModelDrawer = !smartMode && !modelModeDisabled

    useEffect(() => {
        if (smartMode && hasSelectedModel) {
            onClearSelectedModel?.()
        }
    }, [smartMode, hasSelectedModel, onClearSelectedModel])

    useEffect(() => {
        if (!isSameItemList(value || [], itemList)) {
            onItemListChange?.(itemList)
        }
    }, [itemList, onItemListChange, value])

    return (
        <div className={styles.CustomDetailPageConfig}>
            {displayRows.map((row, index) => (
                <div key={row.key || row.item.key}>
                    {row.type === 'item' ? (
                        <div className={styles.Row}>
                            <div className={styles.RowLeft}>
                                {row.item.checked !== undefined && (
                                    <Checkbox
                                        className={styles.CheckboxButton}
                                        checked={row.item.checked}
                                        onChange={() => toggleItemChecked(row.item.key)}
                                    />
                                )}
                                <div className={styles.Label}>{row.item.label}</div>
                            </div>
                            <div className={`${styles.ValueWrap} ${row.item.checked === false ? styles.ValueWrapDisabled : ''}`}>
                                {!row.item.fixed ? (
                                    <CounterAction
                                        type="minus"
                                        disabled={row.item.checked === false || row.item.value <= row.item.min}
                                        onClick={() => updateValue(row.item.key, -1)}
                                    />
                                ) : (
                                    <span className={styles.CounterPlaceholder} />
                                )}
                                <span className={styles.Value}>{row.item.value}</span>
                                {!row.item.fixed ? (
                                    <CounterAction
                                        type="plus"
                                        disabled={row.item.checked === false || row.item.value >= MAX_COUNT}
                                        onClick={() => updateValue(row.item.key, 1)}
                                    />
                                ) : (
                                    <span className={styles.CounterPlaceholder} />
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className={`${styles.ModelModeRow} ${modelModeDisabled ? styles.ModelModeRowDisabled : ''}`}>
                            <div className={styles.ModelModeLeft}>
                                <span className={`${styles.ModelModeText} ${modelModeDisabled ? styles.ModelModeTextDisabled : ''}`}>智能模式</span>
                                <Switch checked={smartMode} disabled={modelModeDisabled} onChange={setSmartMode} />
                            </div>
                            <button
                                type="button"
                                className={`${styles.ModelSettingBtn} ${!canOpenModelDrawer ? styles.ModelSettingBtnDisabled : ''}`}
                                disabled={!canOpenModelDrawer}
                                onClick={onOpenModelDrawer}
                            >
                                {hasSelectedModel && selectedModelUrl && (
                                    <img
                                        className={styles.ModelThumbnail}
                                        src={Utils.getImgThumbnail(selectedModelUrl)}
                                        alt=""
                                    />
                                )}
                                {hasSelectedModel ? '重选' : '设置模特'}
                            </button>
                        </div>
                    )}
                    {index !== displayRows.length - 1 && <div className={styles.Divider} />}
                </div>
            ))}
        </div>
    )
}

export default CustomDetailPageConfig
